import type {
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
  User,
  CreateTenantRequest,
  TenantRegistrationResponse
} from '@tco/shared-types'
import envConfig from '../config/env'

interface AuthTokens {
  accessToken: string
  refreshToken: string
}

class AuthService {
  private static instance: AuthService
  private readonly API_BASE_URL = envConfig.getApiBaseUrl()
  private refreshPromise: Promise<AuthTokens> | null = null

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Storage helpers
  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  }

  private getTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')

    if (!accessToken || !refreshToken) return null

    return { accessToken, refreshToken }
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
  }

  private setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user))
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null

    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  }

  // JWT token helpers
  private parseJwt(token: string): { exp: number; tenant_id?: string; tenantId?: string; sub?: string; email?: string } | null {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch {
      return null
    }
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.parseJwt(token)
    if (!payload) return true

    const now = Date.now() / 1000
    const timeUntilExpiry = payload.exp - now
    const isExpired = payload.exp < now + 30 // 30 seconds buffer

    if (isExpired) {
      console.log(`[AuthService] Token expired or expiring soon (${Math.round(timeUntilExpiry)}s until expiry)`)
    }

    return isExpired
  }

  getTenantId(): string | null {
    // First try to get from token payload
    const tokens = this.getTokens()
    if (tokens) {
      const payload = this.parseJwt(tokens.accessToken)
      if (payload?.tenantId) return payload.tenantId
      if (payload?.tenant_id) return payload.tenant_id
    }

    // Then try from user object
    const user = this.getUser()
    if (user?.tenantId) return user.tenantId
    if ((user as any)?.tenant_id) return (user as any).tenant_id

    return null
  }

  // Auth API calls
  async login(credentials: LoginRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }))
      throw new Error(error.message || 'Invalid credentials')
    }

    const data: LoginResponse = await response.json()

    this.setTokens(data.accessToken, data.refreshToken)
    this.setUser(data.user)

    return data.user
  }

  async register(registrationData: CreateTenantRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/tenants/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registrationData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Registration failed' }))
      throw new Error(error.message || 'Registration failed')
    }

    const data: TenantRegistrationResponse = await response.json()

    // Save tokens from registration response
    this.setTokens(data.access_token, data.refresh_token)

    // Build User object from registration response
    const user: User = {
      id: data.user.id,
      email: data.user.email,
      roleId: '', // Role ID not in response, but role_name is available
      tenantId: data.tenant_id,
      isActive: true,
      role: {
        id: '',
        name: data.user.role_name,
        permissions: []
      }
    }

    this.setUser(user)

    return user
  }

  async logout(): Promise<void> {
    const tokens = this.getTokens()

    if (tokens) {
      try {
        await fetch(`${this.API_BASE_URL}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        })
      } catch {
        // Ignore logout errors - clear local tokens anyway
      }
    }

    this.clearTokens()
  }

  private async refreshTokens(): Promise<AuthTokens> {
    const tokens = this.getTokens()
    if (!tokens) {
      console.warn('[AuthService] No refresh token available')
      throw new Error('No refresh token available')
    }

    const tenantId = this.getTenantId()
    if (!tenantId) {
      console.warn('[AuthService] No tenant ID available for refresh')
      throw new Error('No tenant ID available')
    }

    console.log('[AuthService] Attempting to refresh tokens...')

    try {
      const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId, // FIXED: Added X-Tenant-Id header
        },
        body: JSON.stringify({ refresh_token: tokens.refreshToken } as RefreshRequest),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[AuthService] Token refresh failed:', response.status, errorData)
        this.clearTokens()
        throw new Error(`Token refresh failed: ${errorData.message || response.statusText}`)
      }

      const data: RefreshResponse = await response.json()
      this.setTokens(data.accessToken, data.refreshToken)

      console.log('[AuthService] Tokens refreshed successfully')

      return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      }
    } catch (error) {
      console.error('[AuthService] Token refresh error:', error)
      this.clearTokens()
      throw error
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const tokens = this.getTokens()
    if (!tokens) {
      console.warn('[AuthService] No tokens available')
      return null
    }

    // If token is not expired, return it
    if (!this.isTokenExpired(tokens.accessToken)) {
      return tokens.accessToken
    }

    console.log('[AuthService] Access token expired, attempting refresh...')

    // Prevent multiple concurrent refresh attempts
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshTokens().finally(() => {
        this.refreshPromise = null
      })
    }

    try {
      const newTokens = await this.refreshPromise
      return newTokens.accessToken
    } catch (error) {
      console.error('[AuthService] Failed to get valid access token:', error)
      this.clearTokens()
      return null
    }
  }


  // Check if user is authenticated
  isAuthenticated(): boolean {
    const tokens = this.getTokens()
    const user = this.getUser()
    return !!(tokens && user)
  }

  // Auto-refresh setup for background token refresh
  startAutoRefresh(): () => void {
    console.log('[AuthService] Starting auto-refresh mechanism (checking every minute)')

    const interval = setInterval(async () => {
      const tokens = this.getTokens()
      if (!tokens) return

      // Refresh if token expires in next 5 minutes
      const payload = this.parseJwt(tokens.accessToken)
      if (payload) {
        const now = Date.now() / 1000
        const timeUntilExpiry = payload.exp - now

        // Log token status every 5 minutes if not about to expire
        if (Math.round(timeUntilExpiry) % 300 === 0) {
          console.log(`[AuthService] Token health check: ${Math.round(timeUntilExpiry / 60)} minutes until expiry`)
        }

        if (timeUntilExpiry < 300) { // 5 minutes
          console.warn(`[AuthService] Token expiring soon (${Math.round(timeUntilExpiry)}s), triggering proactive refresh...`)
          try {
            await this.getValidAccessToken()
            console.log('[AuthService] Proactive token refresh successful')
          } catch (error) {
            console.error('[AuthService] Proactive token refresh failed:', error)
            // Token refresh failed - user will be logged out on next API call
          }
        }
      }
    }, 60000) // Check every minute

    return () => {
      console.log('[AuthService] Stopping auto-refresh mechanism')
      clearInterval(interval)
    }
  }
}

export default AuthService